/** @jsx isolateComponent */

import Downshift from 'downshift';
import { css } from '@emotion/core';
import { ReactNode, useRef, FocusEvent } from 'react';
import { Icon } from '../../display/Icon';
import { FloatingMenu, FloatingMenuProps } from '../../forms/FloatingMenu';
import { Input, InputProps } from '../../forms/Input';
import { Colors } from '../../../theme';
import { camelCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { omit } from '../../../utils/omit';

export type FloatingMenuRenderProps<T> = {
  /**
   * Action to manually select an item
   */
  selectItem: (item: T) => void;

  /**
   * The currently selected item (if any)
   */
  selectedItem: T;

  /**
   * Set the currently highlighted item index
   */
  setHighlightedIndex: (index: number) => void;

  /**
   * Clear the selection
   */
  clearSelection: () => void;

  /**
   * Close the menu
   */
  closeMenu: () => void;
} & FloatingMenuProps;

export type OnBlurMetadata = {
  /**
   * Did the blur occur within the dropdown control?
   * i.e. is some child of the Dropdown still focused?
   */
  didBlurWithin: boolean;

  /**
   * Close the menu
   */
  closeMenu: () => void;
};

export type Props<T = any> = {
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.  Defaults to the index of each item in the array.
   */
  getKeyForItem?: (item: T) => string;

  /**
   * Callback that will be used to generate the string label to be rendered for
   * each item on the menu list.
   */
  getLabelForItem: (item: T) => string;

  /**
   * HTML `id` attribute for the `button` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;

  /**
   * The list of items.
   */
  items: Array<T>;

  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel?: string;

  /**
   * Callback executed when the input is blurred.
   */
  onBlur?: (event: FocusEvent<HTMLElement>, meta: OnBlurMetadata) => void;

  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange?: (item: T) => void;

  /**
   * Callback executed when the input is focused.
   */
  onFocus?: (event: FocusEvent<HTMLElement>) => void;

  /**
   * Callback executed when the input is clicked.
   */
  onClick?: () => void;

  /**
   * Callback that gets called when the user selects an item (even if it hasn't changed)
   */
  onSelect?: (item: T) => void;

  /**
   * Optional prop for setting the initially selected item, but still letting `Downshift` manage
   * the selected item state for you.
   */
  initialSelectedItem?: T;

  /**
   * A render prop which receives a menu `item` and returns a React element,
   * which is an icon illustrating the given menu item. The natural size for
   * the icon is 30x30px. If not provided, no icons will be rendered.
   */
  renderIcon?: (item: T) => ReactNode;

  /**
   * A render prop for creating the floating menu.
   */
  renderFloatingMenu: (a: FloatingMenuRenderProps<T>) => ReactNode;

  /**
   * A render prop for the button content given the selected item
   */
  renderButtonForItem?: (item: T) => ReactNode;

  /**
   * Optionally use selectedItem to make Dropdown a controlled component
   */
  selectedItem?: T;
} & Omit<
  InputProps,
  'children' | 'onBlur' | 'onChange' | 'onClick' | 'onFocus'
>;

const isDisabled = (props: Props) => {
  return props.isDisabled || props.items.length === 0;
};

const Styles = labelStyles('Dropdown', {
  root: css({
    position: 'relative',
  }),
  buttonWrap: (props: Props) => ({
    cursor: isDisabled(props) ? 'default' : 'pointer',
  }),
  button: css({
    maxWidth: '100%',
    overflow: 'hidden',
  }),
  buttonText: css({
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  placeholder: css({
    color: Colors.neutral600,
  }),
});

const buildAriaLabel = ({
  label,
  value,
  placeholder,
}: {
  label: string;
  value?: string;
  placeholder?: string;
}) => {
  return [value, label, !value && placeholder].filter(Boolean).join(', ');
};

const defaultProps = {
  size: 'small',
  getLabelForItem: (item: { label: string }) => item.label,
  renderFloatingMenu: (props: any) => <FloatingMenu {...props} />,
  menuAriaLabel: 'List of items',
};

/**
 * Basic dropdown component that should be used when a smallish
 * number of items need to be chosen from, roughly fewer than 20.
 * If there are substantially more items, consider `Typeahead`.
 */
export function Dropdown<T = any>(_props: Props<T>) {
  const props = {
    ...defaultProps,
    ..._props,
  };

  const parentRef = useRef(null);

  const itemToString = (item?: T) => {
    return item ? props.getLabelForItem(item) : '';
  };

  const onChangeNoNull = (selection: T) => {
    if (selection) {
      // eslint-disable-next-line no-unused-expressions
      props.onChange?.(selection);
    }
  };

  const onSelectNoNull = (selection: T) => {
    if (selection) {
      // eslint-disable-next-line no-unused-expressions
      props.onSelect?.(selection);
    }
  };

  const stateReducer = (_: any, changes: any) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEscape:
        // When you open the dropdown and press `esc`, it will clear the selection by default
        // We don't want this functionality, so overriding it here.
        // See: https://github.com/downshift-js/downshift/issues/680
        return {
          isOpen: false,
        };

      default:
        return changes;
    }
  };

  const renderButtonForItem =
    props.renderButtonForItem || props.getLabelForItem;

  return (
    <Downshift
      initialSelectedItem={props.initialSelectedItem}
      inputId={props.inputId}
      itemCount={props.items.length}
      itemToString={itemToString}
      onChange={onChangeNoNull}
      onSelect={onSelectNoNull}
      stateReducer={stateReducer}
      {...(props.selectedItem ? { selectedItem: props.selectedItem } : {})}
    >
      {({
        clearSelection,
        closeMenu,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        isOpen,
        openMenu,
        selectItem,
        selectedItem,
        setHighlightedIndex,
      }) => {
        const hasSelectedItem = !!selectedItem;

        const renderIcon = () =>
          typeof props.renderIcon === 'function'
            ? props.renderIcon(selectedItem)
            : undefined;

        // Remove DOM attrs that Downshift adds but we add manually for accessibility.
        // These should be redundant given `ariaLabel` and `inputId`, and because
        // we're adding it on the wrapping div and passing the appropriate value.
        const rootProps = omit(getRootProps(), ['aria-labelledby']);

        const onBlur = (e: FocusEvent<HTMLElement>) => {
          if (props.onBlur) {
            const meta = {
              didBlurWithin: parentRef.current
                ? (parentRef.current! as HTMLElement).contains(
                    e.relatedTarget as Node
                  )
                : false,
              closeMenu,
            };
            props.onBlur(e, meta);
          } else {
            // If there is no custom onBlur behavior, then just close the menu
            closeMenu();
          }
        };

        const { onChange, onClick, ...propsNoOnChangeOrOnClick } = props;

        return (
          <div
            aria-label={props.menuAriaLabel}
            aria-labelledby={props.inputId}
            css={Styles.root}
            {...rootProps}
            onBlur={onBlur}
            ref={parentRef}
          >
            <div css={Styles.buttonWrap(props)}>
              <Input
                onClick={() => {
                  if (isOpen === true) {
                    closeMenu();
                  } else if (!isDisabled(props)) {
                    openMenu();
                  }

                  if (props.onClick) props.onClick();
                }}
                {...propsNoOnChangeOrOnClick}
                {...camelCaseAriaAttrs(getInputProps())}
                canClickIconAfter={false}
                id={props.inputId}
                isDisabled={isDisabled(props)}
                renderIconAfter={() => <Icon name="arrowDropdown" size={24} />}
                renderIconBefore={renderIcon}
              >
                {(inputProps) => (
                  // Intentionally `button` and not `Button` since it looks
                  // and behaves differently.
                  <button
                    {...{
                      // `Downshift` requires this attribute to work properly, so
                      // there's an a11y balance to perform here. This is spread as
                      // an object since the `eslint-disable-next-line` comment didn't
                      // work for JSX props.
                      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
                      'aria-activedescendant':
                        inputProps['aria-activedescendant'],
                      'aria-invalid': inputProps['aria-invalid'],
                    }}
                    aria-controls={inputProps['aria-controls']}
                    aria-expanded={isOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    aria-label={buildAriaLabel({
                      label: inputProps['aria-label'] || '',
                      placeholder: props.placeholder,
                      value: hasSelectedItem
                        ? props.getLabelForItem(selectedItem)
                        : undefined,
                    })}
                    css={[inputProps.css, Styles.button]}
                    disabled={inputProps.disabled}
                    id={inputProps.id}
                    onBlur={onBlur}
                    onFocus={props.onFocus}
                    onKeyDown={inputProps.onKeyDown}
                    type="button"
                  >
                    {hasSelectedItem ? (
                      <span css={Styles.buttonText}>
                        {renderButtonForItem(selectedItem)}
                      </span>
                    ) : (
                      <span css={[Styles.buttonText, Styles.placeholder]}>
                        {props.placeholder}
                      </span>
                    )}
                  </button>
                )}
              </Input>
            </div>
            {isOpen &&
              props.renderFloatingMenu({
                ariaLabel: props.menuAriaLabel,
                clearSelection,
                closeMenu,
                items: props.items,
                highlightedIndex,
                getKeyForItem: props.getKeyForItem,
                getLabelForItem: props.getLabelForItem,
                getItemProps,
                getMenuProps,
                getInputProps,
                renderIcon: props.renderIcon,
                selectItem,
                selectedItem,
                setHighlightedIndex,
              })}
          </div>
        );
      }}
    </Downshift>
  );
}

Dropdown.defaultProps = defaultProps;
